import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export type ViewLayoutMode = 'stacked' | 'tabs';

const LAYOUT_PARAM = 'layout';

/**
 * Persists the chosen view layout (stacked vs. tabbed) in the `?layout` query
 * param so the choice survives reloads and is shareable. Tabbed is the default
 * and clears the param so canonical URLs stay clean.
 */
export function useViewLayoutMode(): {
  mode: ViewLayoutMode;
  toggle: () => void;
  setMode: (mode: ViewLayoutMode) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const mode: ViewLayoutMode = searchParams.get(LAYOUT_PARAM) === 'stacked' ? 'stacked' : 'tabs';

  const setMode = useCallback(
    (next: ViewLayoutMode) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === 'stacked') {
            params.set(LAYOUT_PARAM, 'stacked');
            // The selected-tab param is meaningless once the layout is stacked.
            params.delete('tab');
          } else {
            params.delete(LAYOUT_PARAM);
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toggle = useCallback(() => {
    setMode(mode === 'tabs' ? 'stacked' : 'tabs');
  }, [mode, setMode]);

  return { mode, toggle, setMode };
}
